// F-079 — Trésorerie, rendement et estimation fiscale : trois vues distinctes, formules affichées.
import { useState, type ReactNode } from "react";
import { useGet } from "@core/hooks";
import { date, money } from "@core/format";
import { useOrg } from "@core/org";
import { Card, Empty, ErrorBox, KeyValues, Loading, Notice, PageHeader, Select, Table, Tabs } from "@core/ui";
import { YieldCard } from "./operations";
import { Amount, MonthlyChart, PropertySelect, YearSelect, currentYear, useRefs } from "./shared";

type View = "tresorerie" | "rendement" | "fiscalite";

export function TreasuryPage() {
  const [view, setView] = useState<View>("tresorerie");
  const [year, setYear] = useState(currentYear());
  return (
    <div className="page">
      <PageHeader
        eyebrow="Finances"
        title="Trésorerie, rendement et fiscalité"
        intro="Trois vues séparées : les flux réellement encaissés et payés, le rendement de chaque logement et une estimation fiscale indicative. Aucune ne remplace les deux autres."
      />
      <div className="row-between">
        <Tabs
          value={view}
          onChange={setView}
          items={[
            { value: "tresorerie", label: "Trésorerie" },
            { value: "rendement", label: "Rendement" },
            { value: "fiscalite", label: "Estimation fiscale" },
          ]}
        />
        <div style={{ minWidth: 160 }}>
          <YearSelect value={year} onChange={setYear} />
        </div>
      </div>
      {view === "tresorerie" ? <Cashflow year={year} /> : view === "rendement" ? <Yields year={year} /> : <TaxEstimate year={year} />}
    </div>
  );
}

type Month = {
  month: number;
  label: string;
  rent_calls_cents: number;
  rent_collected_cents: number;
  unallocated_cents: number;
  other_income_cents: number;
  operating_expenses_cents: number;
  investment_cents: number;
  financing_cents: number;
  net_cents: number;
  cumulative_cents: number;
  future: boolean;
};

function Cashflow({ year }: { year: number }) {
  const org = useOrg();
  const refs = useRefs();
  const [propertyId, setPropertyId] = useState("");
  const q = useGet<{ months: Month[]; totals: Record<string, number>; notes: string[] }>(org.api(`/finance/cashflow?year=${year}${propertyId ? `&property_id=${propertyId}` : ""}`));
  const t = q.data?.totals;
  return (
    <div className="stack">
      <div style={{ maxWidth: 360 }}>
        <PropertySelect refs={refs.data} value={propertyId} onChange={setPropertyId} empty="Tout le patrimoine" />
      </div>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && t ? (
        <>
          <div className="stats">
            <div className="stat">
              <span className="stat-label">Loyers appelés</span>
              <span className="stat-value">{money(t.rent_calls_cents)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Loyers et charges encaissés</span>
              <span className="stat-value">{money(t.rent_collected_cents)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Dépenses (exploitation et investissement)</span>
              <span className="stat-value">{money(t.operating_expenses_cents + t.investment_cents)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Financement</span>
              <span className="stat-value">{money(t.financing_cents)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Solde de trésorerie</span>
              <span className="stat-value">{money(t.net_cents)}</span>
              <span className="stat-hint">Flux, pas un résultat fiscal</span>
            </div>
          </div>
          <Card title={`Flux mensuels ${year}`}>
            <MonthlyChart
              caption={`Encaissements, paiements et solde cumulé par mois en ${year}. Valeurs détaillées dans le tableau.`}
              rows={q.data.months.map((m) => ({
                label: m.label,
                income: m.rent_collected_cents + m.unallocated_cents + m.other_income_cents,
                expense: m.operating_expenses_cents + m.investment_cents + m.financing_cents,
                cumulative: m.cumulative_cents,
              }))}
            />
            <Table caption="Flux mensuels">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th className="right">Loyers appelés</th>
                  <th className="right">Encaissé</th>
                  <th className="right">Non ventilé</th>
                  <th className="right">Autres recettes</th>
                  <th className="right">Exploitation</th>
                  <th className="right">Investissement</th>
                  <th className="right">Financement</th>
                  <th className="right">Solde</th>
                  <th className="right">Cumul</th>
                </tr>
              </thead>
              <tbody>
                {q.data.months.map((m) => (
                  <tr key={m.month} className={m.future ? "muted" : ""}>
                    <td>{m.label}</td>
                    <td className="right"><Amount cents={m.rent_calls_cents} /></td>
                    <td className="right"><Amount cents={m.rent_collected_cents} /></td>
                    <td className="right"><Amount cents={m.unallocated_cents} /></td>
                    <td className="right"><Amount cents={m.other_income_cents} /></td>
                    <td className="right"><Amount cents={m.operating_expenses_cents} /></td>
                    <td className="right"><Amount cents={m.investment_cents} /></td>
                    <td className="right"><Amount cents={m.financing_cents} /></td>
                    <td className="right"><Amount cents={m.net_cents} /></td>
                    <td className="right"><Amount cents={m.cumulative_cents} /></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
          <Card title="Comment lire cette vue">
            <ul className="finances-formules">
              {q.data.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function Yields({ year }: { year: number }) {
  const org = useOrg();
  const q = useGet<{ items: any[]; formulas: string[]; period: { from: string; to: string } }>(org.api(`/finance/yield?year=${year}`));
  return (
    <div className="stack">
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data && q.data.items.length === 0 ? <Empty title="Aucun logement dans cet espace." /> : null}
      {q.data ? (
        <>
          <Notice>
            Rendement calculé sur les données enregistrées du {date(q.data.period.from)} au {date(q.data.period.to)}. Un rendement n’est pas une valorisation et ne préjuge pas de l’imposition.
          </Notice>
          <div className="grid-2">
            {q.data.items.map((it) => (
              <YieldCard key={it.property_id} item={it} orgId={org.orgId} canEdit={org.can("finance")} />
            ))}
          </div>
          <Card title="Formules et exclusions">
            <ul className="finances-formules">
              {q.data.formulas.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}

type TaxResponse = {
  inputs: { gross_income_cents: number; deductible_charges_cents: number; interest_cents: number; units: number; tmi_pct: number | null; general_expenses_included_cents: number };
  excluded: { furnished_rents_cents: number; non_deductible_cents: number; non_deductible_detail: { category: string; label: string; amount_cents: number }[] };
  estimate: {
    micro_eligible: boolean;
    micro_abatement_cents: number;
    micro_taxable_cents: number;
    reel_flat_fees_cents: number;
    reel_result_cents: number;
    reel_taxable_cents: number;
    deficit_imputable_cents: number;
    deficit_carried_forward_cents: number;
    lower_taxable: "micro" | "reel" | "equivalent" | "reel_seul";
    micro_tax: { income_tax_cents: number; social_contributions_cents: number; total_cents: number } | null;
    reel_tax: { income_tax_cents: number; social_contributions_cents: number; total_cents: number } | null;
  };
  notices: string[];
  disclaimer: string;
};

function TaxEstimate({ year }: { year: number }) {
  const org = useOrg();
  const refs = useRefs();
  const [tmi, setTmi] = useState("");
  const [owner, setOwner] = useState("");
  const params = new URLSearchParams({ year: String(year) });
  if (tmi) params.set("tmi", tmi);
  if (owner) params.set("owner_id", owner);
  const q = useGet<TaxResponse>(org.api(`/finance/tax-estimate?${params}`));
  const d = q.data;
  const e = d?.estimate;
  const conclusion = !e
    ? ""
    : e.lower_taxable === "reel_seul"
      ? "Les revenus bruts enregistrés dépassent 15 000 € : le micro-foncier ne serait pas accessible."
      : e.lower_taxable === "reel"
        ? "Selon les seules opérations enregistrées, le revenu imposable serait plus faible au régime réel."
        : e.lower_taxable === "micro"
          ? "Selon les seules opérations enregistrées, le revenu imposable serait plus faible au micro-foncier."
          : "Selon les seules opérations enregistrées, les deux régimes donneraient un revenu imposable équivalent.";
  return (
    <div className="stack">
      <Notice tone="attention" title="Estimation indicative, pas un conseil">
        {d?.disclaimer ?? "Estimation indicative, non contractuelle."} Revenus fonciers des locations nues uniquement.
      </Notice>
      <div className="finances-filtres">
        <Select
          label="Tranche marginale d’imposition (facultatif)"
          value={tmi}
          onChange={(ev) => setTmi(ev.target.value)}
          options={[{ value: "", label: "Non renseignée" }, ...[0, 11, 30, 41, 45].map((t) => ({ value: String(t), label: `${t} %` }))]}
        />
        {(refs.data?.owners.length ?? 0) > 1 ? (
          <Select label="Propriétaire" value={owner} onChange={(ev) => setOwner(ev.target.value)} options={[{ value: "", label: "Tous" }, ...(refs.data?.owners ?? []).map((o) => ({ value: o.id, label: o.display_name }))]} />
        ) : null}
      </div>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {d && e ? (
        <>
          <div className="finances-deux-colonnes">
            <section className={`finances-colonne ${e.lower_taxable === "micro" ? "finances-colonne-retenue" : ""}`} aria-label="Micro-foncier">
              <h3>Micro-foncier</h3>
              {e.micro_eligible ? (
                <KeyValues
                  items={[
                    ["Loyers bruts encaissés", <Amount cents={d.inputs.gross_income_cents} />],
                    ["Abattement forfaitaire de 30 %", <Amount cents={-e.micro_abatement_cents} />],
                    ["Revenu imposable estimé", <strong><Amount cents={e.micro_taxable_cents} /></strong>],
                    ...(e.micro_tax ? ([["Impôt et prélèvements estimés", <Amount cents={e.micro_tax.total_cents} />]] as [string, ReactNode][]) : []),
                  ]}
                />
              ) : (
                <p className="muted">Non accessible au-delà de 15 000 € de revenus fonciers bruts.</p>
              )}
            </section>
            <section className={`finances-colonne ${e.lower_taxable === "reel" || e.lower_taxable === "reel_seul" ? "finances-colonne-retenue" : ""}`} aria-label="Régime réel">
              <h3>Régime réel</h3>
              <KeyValues
                items={[
                  ["Loyers bruts encaissés", <Amount cents={d.inputs.gross_income_cents} />],
                  ["Charges déductibles (part récupérée exclue)", <Amount cents={-d.inputs.deductible_charges_cents} />],
                  [`Frais de gestion forfaitaires (${d.inputs.units} local${d.inputs.units > 1 ? "aux" : ""})`, <Amount cents={-e.reel_flat_fees_cents} />],
                  ["Intérêts et assurance d’emprunt", <Amount cents={-d.inputs.interest_cents} />],
                  ["Résultat foncier", <Amount cents={e.reel_result_cents} />],
                  ["Revenu imposable estimé", <strong><Amount cents={e.reel_taxable_cents} /></strong>],
                  ...(e.deficit_imputable_cents > 0
                    ? ([
                        ["Déficit imputable sur le revenu global", <Amount cents={e.deficit_imputable_cents} />],
                        ["Déficit reportable", <Amount cents={e.deficit_carried_forward_cents} />],
                      ] as [string, ReactNode][])
                    : []),
                  ...(e.reel_tax ? ([["Impôt et prélèvements estimés", <Amount cents={e.reel_tax.total_cents} />]] as [string, ReactNode][]) : []),
                ]}
              />
            </section>
          </div>
          <p>
            <strong>{conclusion}</strong> Vous décidez, idéalement avec un professionnel.
          </p>
          <Card title="Exclus de l’estimation">
            <KeyValues
              items={[
                ["Loyers de location meublée", <Amount cents={d.excluded.furnished_rents_cents} />],
                ["Dépenses non déductibles ou hors charges (capital, agrandissement…)", <Amount cents={d.excluded.non_deductible_cents} />],
                ...d.excluded.non_deductible_detail.map((x) => [x.label, <Amount cents={x.amount_cents} />] as [string, ReactNode]),
              ]}
            />
          </Card>
          <Card title="Hypothèses et limites">
            <ul className="finances-formules">
              {d.notices.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          </Card>
        </>
      ) : null}
    </div>
  );
}
